import { Attachment, Task } from '@/types';
import { z } from 'zod';

const STORAGE_KEY = 'psa_tasks';
const VERSION_KEY = 'psa_tasks_version';
const CURRENT_VERSION = '1.0';

// Task validation schema
// const TaskSchema = z.object({
//   id: z.string(),
//   title: z.string().min(1, "Title is required"),
//   description: z.string(),
//   assignedTo: z.string().min(1, "Student ID is required"),
//   interventionId: z.string().min(1, "Intervention ID is required"),
//   dueDate: z.string().datetime(),
//   status: z.enum(['not_started', 'in_process', 'completed', 'frozen']),
//   priority: z.enum(['high', 'medium', 'low']),
//   category: z.enum(['academic', 'attendance', 'behavioral', 'health']),
//   createdAt: z.string().datetime(),
//   updatedAt: z.string().datetime(),
//   comments: z.array(z.object({
//     id: z.string(),
//     userId: z.string(),
//     content: z.string(),
//     timestamp: z.string().datetime()
//   })),
//   reminders: z.array(z.string().datetime()),
//   attachments: z.array(z.string())
// });

type Comment = {
  id: string
  userId: string
  content: string
  timestamp: string
}

type ValidatedTask = {
  id: string
  title: string
  description: string
  assignedTo: string
  interventionId: string
  dueDate: string
  status: 'not_started' | 'in_process' | 'completed' | 'frozen'
  priority: 'high' | 'medium' | 'low'
  category: 'academic' | 'attendance' | 'behavioral' | 'health' | 'discipline'
  createdAt: string
  updatedAt: string
  comments: Comment[]
  reminders: string[]
  attachments: Attachment[]
}

interface TaskLog {
  timestamp: string;
  action: 'create' | 'update' | 'delete' | 'error';
  taskId: string;
  details: string;
}

class TaskStorageService {
  private static instance: TaskStorageService;
  private logs: TaskLog[] = [];

  private constructor() {
    this.initializeStorage();
  }

  public static getInstance(): TaskStorageService {
    if (!TaskStorageService.instance) {
      TaskStorageService.instance = new TaskStorageService();
    }
    return TaskStorageService.instance;
  }

  private initializeStorage(): void {
    try {
      const version = localStorage.getItem(VERSION_KEY);
      if (version !== CURRENT_VERSION) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
        localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
      }

      // Validate existing tasks
      this.validateAllTasks();
    } catch (error) {
      this.logError('initialization', 'system', `Storage initialization failed: ${error}`);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
      localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
    }
  }

  private validateAllTasks(): void {
    try {
      const tasks = this.getTasks();
      const validatedTasks = tasks.map(task => {
        try {
          return task;
        } catch (error) {
          this.logError('validation', task.id, `Task validation failed: ${error}`);
          return null;
        }
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(validatedTasks));
    } catch (error) {
      this.logError('validation', 'system', `Bulk validation failed: ${error}`);
    }
  }

  private validateTask(task: Task): ValidatedTask {
    try {
      return {
        ...task,
        updatedAt: new Date().toISOString(),
        interventionId: task.interventionId || 'unknown'
      };
    } catch (error) {
      throw new Error(`Task validation failed: ${error}`);
    }
  }

  private logAction(action: TaskLog['action'], taskId: string, details: string): void {
    const log: TaskLog = {
      timestamp: new Date().toISOString(),
      action,
      taskId,
      details
    };
    this.logs.push(log);
    console.log(`[Task Storage] ${action.toUpperCase()}: ${details} (Task ID: ${taskId})`);
  }

  private logError(action: string, taskId: string, error: string): void {
    this.logAction('error', taskId, `${action} failed: ${error}`);
  }

  public getTasks(): Task[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return [];
      return JSON.parse(data);
    } catch (error) {
      this.logError('retrieval', 'system', `Failed to get tasks: ${error}`);
      return [];
    }
  }

  public getTasksByIntervention(interventionId: string): Task[] {
    return this.getTasks().filter(task => task.interventionId === interventionId);
  }

  public getTasksByStudent(studentId: string): Task[] {
    return this.getTasks().filter(task => task.assignedTo === studentId);
  }

  public saveTask(task: Task): Task | null {
    try {
      // Validate relationships
      if (!task.assignedTo) {
        throw new Error('Task must be assigned to a student');
      }
      if (!task.interventionId) {
        throw new Error('Task must be linked to an intervention');
      }

      const validatedTask = this.validateTask(task);
      const tasks = this.getTasks();
      const updatedTasks = [...tasks, validatedTask];
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTasks));
      this.logAction('create', task.id, 'Task created successfully');
      
      return validatedTask;
    } catch (error) {
      this.logError('save', task.id, `${error}`);
      return null;
    }
  }

  public updateTask(task: Task): Task | null {
    try {
      const validatedTask = this.validateTask(task);
      const tasks = this.getTasks();
      const taskIndex = tasks.findIndex(t => t.id === task.id);
      
      if (taskIndex === -1) {
        throw new Error('Task not found');
      }

      const updatedTasks = tasks.map(t => 
        t.id === task.id ? { ...validatedTask, updatedAt: new Date().toISOString() } : t
      );
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTasks));
      this.logAction('update', task.id, 'Task updated successfully');
      
      return validatedTask;
    } catch (error) {
      this.logError('update', task.id, `${error}`);
      return null;
    }
  }

  public deleteTask(taskId: string): boolean {
    try {
      const tasks = this.getTasks();
      const updatedTasks = tasks.filter(task => task.id !== taskId);
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTasks));
      this.logAction('delete', taskId, 'Task deleted successfully');
      
      return true;
    } catch (error) {
      this.logError('delete', taskId, `${error}`);
      return false;
    }
  }

  public getLogs(): TaskLog[] {
    return [...this.logs];
  }

  public clearStorage(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VERSION_KEY);
    this.logs = [];
    this.initializeStorage();
  }

  // Recovery methods
  public async attemptRecovery(taskId: string): Promise<boolean> {
    try {
      const tasks = this.getTasks();
      const task = tasks.find(t => t.id === taskId);
      
      if (!task) {
        throw new Error('Task not found for recovery');
      }

      const validatedTask = this.validateTask(task);
      return !!this.updateTask(validatedTask);
    } catch (error) {
      this.logError('recovery', taskId, `Recovery attempt failed: ${error}`);
      return false;
    }
  }
}

export const taskStorage = TaskStorageService.getInstance();